import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Server,
  Layout,
  Cloud,
  Database,
  Brain,
  Zap,
  Shield,
  ArrowRight,
  Check,
} from 'lucide-react';
import { Container, Section, Badge, Card } from '../components/ui';
import { CTA, PageHero } from '../components/sections';
import { SERVICES } from '../constants';

const iconMap: Record<string, React.ElementType> = {
  Sparkles,
  Server,
  Layout,
  Cloud,
  Database,
  Brain,
  Zap,
  Shield,
};

const engagementModels = [
  {
    title: 'Project-Based',
    description: 'End-to-end project delivery with fixed scope, timeline, and budget.',
    features: ['Fixed cost', 'Defined deliverables', 'Milestone-based payments'],
  },
  {
    title: 'Staff Augmentation',
    description: 'Extend your team with our expert developers on a contract basis.',
    features: ['Flexible duration', 'Seamless integration', 'Your management'],
  },
  {
    title: 'Dedicated Team',
    description: 'A fully dedicated team working exclusively on your projects.',
    features: ['Full-time commitment', 'Scalable team size', 'Long-term partnership'],
  },
];

export const Services: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <PageHero
        badge={
          <Badge className="mb-6 bg-white/10 text-white border border-white/20">
            Our Services
          </Badge>
        }
        title="Comprehensive Technology Solutions"
        subtitle="From AI innovation to cloud infrastructure, we deliver end-to-end technology services that drive business transformation."
      />

      {/* Services Grid */}
      <Section background="white" padding="lg">
        <Container>
          <div className="grid md:grid-cols-2 gap-8">
            {SERVICES.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Sparkles;
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card hover className="h-full">
                    <div className="flex gap-6">
                      {/* Icon */}
                      <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-navy-100 to-accent-50 flex items-center justify-center">
                        <IconComponent className="w-8 h-8 text-navy-700" />
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-navy-900 mb-2">
                          {service.title}
                        </h3>
                        <p className="text-navy-600 mb-4">
                          {service.description}
                        </p>

                        {/* Features */}
                        {service.features && (
                          <div className="space-y-2 mb-4">
                            {service.features.map((feature) => (
                              <div key={feature} className="flex items-center gap-2 text-sm text-navy-500">
                                <Check className="w-4 h-4 text-accent-500" />
                                {feature}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Link */}
                        <Link
                          to={`/services/${service.id}`}
                          className="inline-flex items-center gap-2 text-accent-600 font-medium hover:text-accent-700 group"
                        >
                          Learn more
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* Engagement Models */}
      <Section background="mesh" padding="lg">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="accent" className="mb-4">Engagement Models</Badge>
            <h2 className="heading-lg text-navy-900 mb-4">
              Flexible Ways to Work Together
            </h2>
            <p className="text-body text-lg">
              Choose the engagement model that best fits your needs and organizational structure.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {engagementModels.map((model, index) => (
              <motion.div
                key={model.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full text-center">
                  <h3 className="text-xl font-semibold text-navy-900 mb-3">
                    {model.title}
                  </h3>
                  <p className="text-navy-600 mb-6">
                    {model.description}
                  </p>
                  <ul className="space-y-2">
                    {model.features.map((feature) => (
                      <li key={feature} className="flex items-center justify-center gap-2 text-sm text-navy-500">
                        <Check className="w-4 h-4 text-accent-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Process Section */}
      <Section background="white" padding="lg">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="primary" className="mb-4">Our Process</Badge>
            <h2 className="heading-lg text-navy-900 mb-4">
              How We Work
            </h2>
            <p className="text-body text-lg">
              A proven methodology that ensures successful project delivery.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', description: 'Understanding your business, goals, and technical requirements.' },
              { step: '02', title: 'Planning', description: 'Creating a detailed roadmap with milestones and deliverables.' },
              { step: '03', title: 'Execution', description: 'Agile development with regular updates and iterations.' },
              { step: '04', title: 'Delivery', description: 'Deployment, knowledge transfer, and ongoing support.' },
            ].map((phase, index) => (
              <motion.div
                key={phase.step}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                {/* Connector Line */}
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-navy-200" />
                )}
                
                <div className="relative text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-navy-900 text-white flex items-center justify-center text-xl font-bold">
                    {phase.step}
                  </div>
                  <h3 className="text-lg font-semibold text-navy-900 mb-2">{phase.title}</h3>
                  <p className="text-navy-600 text-sm">{phase.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      <CTA />
    </>
  );
};

